import { createListCollection, Field, Select, Span } from "@chakra-ui/react";
import useThemeColors from "../../hooks/useThemeColors";

interface Props {
  placeholder: string;
  invalid?: boolean;
  required?: boolean;
  label?: string;
  name: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onBlur?: () => void;
  error?: string;
  collection: {
    label: string;
    value: string;
  }[];
  size?: "sm" | "md" | "lg";
  minW?: string | {};
  maxW?: string | {};
  value?: string;
  w?: string | {};
  blankOptionLabel?: string;
  blankOptionValue?: string | number;
  disabled?: boolean;
  readOnly?: boolean;
}

function SelectInput({
  name,
  onChange,
  onBlur,
  error,
  invalid,
  required,
  collection,
  placeholder,
  minW,
  maxW,
  value,
  label,
  w,
  blankOptionLabel,
  blankOptionValue = "",
  disabled,
  readOnly = false,
}: Props) {
  const colors = useThemeColors();
  const options = createListCollection({
    items: collection,
  });

  const handleChange = (event: any) => {
    const selectedValue = event?.value?.[0] || "";
    const syntheticEvent = {
      target: {
        name: name,
        value: selectedValue,
      },
    } as React.ChangeEvent<HTMLSelectElement>;
    onChange(syntheticEvent);
  };

  const handleBlur = () => {
    if (onBlur) {
      onBlur();
    }
  };

  if (error) {
    return (
      <Field.Root invalid={invalid} required={required} w={w} >
        <Select.Root
          invalid={invalid}
          required={required}
          collection={options}
          onValueChange={handleChange}
          name={name}
          minW={minW}
          maxW={maxW}
          value={value ? [value] : []}
          positioning={{ sameWidth: true, placement: "bottom" }}
          disabled={disabled}
          readOnly={readOnly}
        >
          <Select.HiddenSelect />
          {label && (
            <Select.Label>
              {label} <Field.RequiredIndicator />
            </Select.Label>
          )}
          <Select.Control>
            <Select.Trigger
              borderColor={colors.border.input}
              onBlur={handleBlur}
            >
              <Select.ValueText placeholder={placeholder} />
            </Select.Trigger>
            <Select.IndicatorGroup>
              <Select.Indicator />
            </Select.IndicatorGroup>
          </Select.Control>

          <Select.Positioner>
            <Select.Content width="full" >
              {blankOptionLabel && (
                <Select.Item
                  item={{
                    label: blankOptionLabel,
                    value: blankOptionValue?.toString(),
                  }}
                >
                  {blankOptionLabel}
                  <Select.ItemIndicator />
                </Select.Item>
              )}
              {options.items.map((option) => (
                <Select.Item item={option} key={option.value}>
                  {option.label}
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>

          {error && <Field.ErrorText>{error}</Field.ErrorText>}
        </Select.Root>
      </Field.Root>
    );
  }

  return (
    <Select.Root
      invalid={invalid}
      required={required}
      collection={options}
      onValueChange={handleChange}
      name={name}
      minW={minW}
      maxW={maxW}
      value={value ? [value] : []}
      disabled={disabled}
      w={w}
      readOnly={readOnly}
    >
      <Select.HiddenSelect />
      {label && (
        <Select.Label>
          {label} {required && <Span color={"red"}>*</Span>}
        </Select.Label>
      )}
      <Select.Control>
        <Select.Trigger borderColor={colors.border.input} onBlur={handleBlur}>
          <Select.ValueText placeholder={placeholder} />
        </Select.Trigger>
        <Select.IndicatorGroup>
          <Select.Indicator />
        </Select.IndicatorGroup>
      </Select.Control>

      <Select.Positioner>
        <Select.Content width="full">
          {blankOptionLabel && (
            <Select.Item
              item={{
                label: blankOptionLabel,
                value: blankOptionValue?.toString(),
              }}
            >
              {blankOptionLabel}
              <Select.ItemIndicator />
            </Select.Item>
          )}
          {options.items.map((option) => (
            <Select.Item item={option} key={option.value}>
              {option.label}
              <Select.ItemIndicator />
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Positioner>

      {error && <Field.ErrorText>{error}</Field.ErrorText>}
    </Select.Root>
  );
}

export default SelectInput;
